import { FunctionComponent, Suspense, lazy } from "react";
import type { ReactElement } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClientProvider } from "react-query";
import { Box, LinearProgress, Skeleton, Stack } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { Navigation } from "./components";
import { AppRouteFrame } from "./components/AppRouteFrame";
import { AppThemeProvider } from "./theme";
import useAuthSnapshot from "./hooks/useAuthSnapshot";
import { queryClient } from "./queryClient";
import Home from "./pages/Home";
import Flights from "./pages/Flights";
import Flight from "./pages/Flight";
import { getRelativeRoutePath, getRoutePath, getRouteRedirect, resolveRouteAccess } from "./routes/catalog";
import type { RouteAccessContext } from "./routes/catalog";
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminCustomerDataShare = lazy(() => import("./pages/AdminCustomerDataShare"));
const Users = lazy(() => import("./pages/Users"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardFlight = lazy(() => import("./pages/DashboardFlight"));
const Fleets = lazy(() => import("./pages/Fleets"));
const JourneyLogs = lazy(() => import("./pages/JourneyLogs"));
const Ops = lazy(() => import("./pages/Ops"));
const OpsTransferDelay = lazy(() => import("./pages/OpsTransferDelay"));
const OpsEmailDistribution = lazy(() => import("./pages/OpsEmailDistribution"));
const Files = lazy(() => import("./pages/Files"));
const Library = lazy(() => import("./pages/Library"));
const ImproveYourOperation = lazy(() => import("./pages/ImproveYourOperation"));
const AdminLayout = lazy(() => import("./pages/Admin"));
const AdminOverview = lazy(() => import("./pages/AdminOverview"));
const AdminStatus = lazy(() => import("./pages/AdminStatus"));
const AdminMaintenance = lazy(() => import("./pages/AdminMaintenance"));
const AdminOperatorSettings = lazy(() => import("./pages/AdminOperatorSettings"));
const AdminOperatorServices = lazy(() => import("./pages/AdminOperatorServices"));
const AdminAccounting = lazy(() => import("./pages/AdminAccounting"));
const AdminLeonTransfer = lazy(() => import("./pages/AdminLeonTransfer"));
const AdminLeonId = lazy(() => import("./pages/AdminLeonId"));
const AdminEmailDistribution = lazy(() => import("./pages/AdminEmailDistribution"));
const AdminEmailFooter = lazy(() => import("./pages/AdminEmailFooter"));
const AdminLogs = lazy(() => import("./pages/AdminLogs"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminNextclouds = lazy(() => import("./pages/AdminNextclouds"));
const AdminPdf = lazy(() => import("./pages/AdminPdf"));
const AdminMailSettings = lazy(() => import("./pages/AdminMailSettings"));
const RefuelEuWorkspace = lazy(() => import("./features/refuel-eu/pages/RefuelEuWorkspace"));
const LegacyRefuelEuRedirect = lazy(() => import("./features/refuel-eu/pages/LegacyRefuelEuRedirect"));
const Fuel = lazy(() => import("./pages/Fuel"));
const FuelAssuranceOverview = lazy(() => import("./pages/fuel-assurance/FuelAssuranceOverview"));
const FuelAssuranceFlights = lazy(() => import("./pages/fuel-assurance/FuelAssuranceFlights"));
const FuelAssuranceFlightExplorer = lazy(
  () => import("./pages/fuel-assurance/FuelAssuranceFlightExplorer"),
);
const FuelAssuranceDataQuality = lazy(
  () => import("./pages/fuel-assurance/FuelAssuranceDataQuality"),
);
const FuelAssuranceEntities = lazy(() => import("./pages/fuel-assurance/FuelAssuranceEntities"));
const FuelAssuranceTrends = lazy(() => import("./pages/fuel-assurance/FuelAssuranceTrends"));
const FuelAssuranceOpportunities = lazy(
  () => import("./pages/fuel-assurance/FuelAssuranceOpportunities"),
);
const FuelAssuranceScfReadiness = lazy(
  () => import("./pages/fuel-assurance/FuelAssuranceScfReadiness"),
);
const UserProfile = lazy(() => import("./pages/UserProfile"));
const Reports = lazy(() => import("./pages/Reports"));
const ReportFuel = lazy(() => import("./pages/ReportFuel"));
const ReportApproachDeicing = lazy(() => import("./pages/ReportApproachDeicing"));
const ReportOtpTurnaround = lazy(() => import("./pages/ReportOtpTurnaround"));
const ReportOtp = lazy(() => import("./pages/ReportOtp"));
const ReportCrewDuty = lazy(() => import("./pages/ReportCrewDuty"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PreserveQueryRedirect = lazy(() => import("./components/PreserveQueryRedirect"));

const RouteSkeleton: FunctionComponent = () => (
  <Box sx={{ width: "100%" }}>
    <LinearProgress
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
    />
    <Box
      sx={{
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 2.5, md: 4 },
        py: { xs: 2.5, md: 3.5 },
      }}
    >
      <Stack spacing={3}>
        <Skeleton variant="rounded" height={140} sx={{ borderRadius: 4 }} />
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <Skeleton variant="rounded" height={110} sx={{ flex: 1, borderRadius: 3 }} />
          <Skeleton variant="rounded" height={110} sx={{ flex: 1, borderRadius: 3 }} />
          <Skeleton variant="rounded" height={110} sx={{ flex: 1, borderRadius: 3 }} />
        </Stack>
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
      </Stack>
    </Box>
  </Box>
);

const LibraryReportRedirect: FunctionComponent = () => {
  const { id } = useParams();
  return <Navigate to={getRouteRedirect("library-user-report", { id: id || "" })} replace />;
};

const guardRoute = (routeId: string, context: RouteAccessContext, element: ReactElement, deniedRedirectOverride?: string): ReactElement => {
  const resolution = resolveRouteAccess(routeId, context);
  if (resolution.allowed) {
    return element;
  }

  const redirectTo = context.isRefuelEuReader ? resolution.deniedRedirectTo : deniedRedirectOverride || resolution.deniedRedirectTo;
  if (!redirectTo) {
    throw new Error(`Route ${routeId} is denied but has no redirect target`);
  }
  return <Navigate to={redirectTo} replace />;
};

const App: FunctionComponent = () => {
  const { isAuthenticated, isSuperAdmin, isCompanyAdmin, isAcmiClient, isRefuelEuReader, canManageUsers } = useAuthSnapshot();
  const routeAccessContext: RouteAccessContext = {
    isAcmiClient,
    isRefuelEuReader,
    isCompanyAdmin,
    isSuperAdmin,
    canManageUsers,
  };

  return (
    <AppThemeProvider>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Navigation />
            <AppRouteFrame isAuthenticated={isAuthenticated}>
              <Suspense fallback={<RouteSkeleton />}>
                <Routes>
                <Route path={getRoutePath("home")} element={guardRoute("home", routeAccessContext, <Home />)} />
                <Route path={getRoutePath("flights")} element={guardRoute("flights", routeAccessContext, <Flights />)} />
                <Route
                  path={getRoutePath("improve-operation")}
                  element={guardRoute(
                    "improve-operation",
                    routeAccessContext,
                    <ImproveYourOperation />,
                    isAuthenticated ? getRoutePath("ops") : getRoutePath("home"),
                  )}
                />
                <Route path={getRoutePath("files")} element={guardRoute("files", routeAccessContext, <Files />)} />
                <Route path={getRoutePath("library")} element={guardRoute("library", routeAccessContext, <Library />)} />
                <Route path={getRoutePath("flight-detail")} element={guardRoute("flight-detail", routeAccessContext, <Flight />)} />
                <Route path={getRoutePath("reset-password")} element={guardRoute("reset-password", routeAccessContext, <ResetPassword />)} />
                <Route path={getRoutePath("reset-pin")} element={guardRoute("reset-pin", routeAccessContext, <ResetPassword />)} />
                <Route
                  path={getRoutePath("users-legacy")}
                  element={guardRoute("users-legacy", routeAccessContext, <Navigate to={getRouteRedirect("users-legacy")} replace />)}
                />
                <Route path={getRoutePath("dashboard")} element={guardRoute("dashboard", routeAccessContext, <Dashboard />)} />
                <Route path={getRoutePath("dashboard-flight")} element={guardRoute("dashboard-flight", routeAccessContext, <DashboardFlight />)} />
                <Route
                  path={getRoutePath("reports-refuel-legacy")}
                  element={guardRoute(
                    "reports-refuel-legacy",
                    routeAccessContext,
                    <PreserveQueryRedirect to={getRouteRedirect("reports-refuel-legacy")} />,
                  )}
                />
                <Route path={getRoutePath("reports")} element={guardRoute("reports", routeAccessContext, <Reports />)}>
                  <Route path={getRelativeRoutePath("report-fuel")} element={guardRoute("report-fuel", routeAccessContext, <ReportFuel />)} />
                  <Route
                    path={getRelativeRoutePath("report-approach-deicing")}
                    element={guardRoute("report-approach-deicing", routeAccessContext, <ReportApproachDeicing />)}
                  />
                  <Route path={getRelativeRoutePath("report-crew-duty")} element={guardRoute("report-crew-duty", routeAccessContext, <ReportCrewDuty />)} />
                  <Route
                    path={getRelativeRoutePath("report-otp")}
                    element={guardRoute("report-otp", routeAccessContext, <ReportOtp />)}
                  />
                  <Route
                    path={getRelativeRoutePath("report-otp-day")}
                    element={guardRoute("report-otp-day", routeAccessContext, <ReportOtp />)}
                  />
                  <Route
                    path={getRelativeRoutePath("report-otp-turnaround")}
                    element={guardRoute("report-otp-turnaround", routeAccessContext, <ReportOtpTurnaround />)}
                  />
                </Route>
                <Route path={getRoutePath("fuel")} element={guardRoute("fuel", routeAccessContext, <Fuel />)}>
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu")}
                    element={guardRoute("fuel-refuel-eu", routeAccessContext, <LegacyRefuelEuRedirect />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu-unjustified")}
                    element={guardRoute("fuel-refuel-eu-unjustified", routeAccessContext, <LegacyRefuelEuRedirect />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu-year")}
                    element={guardRoute("fuel-refuel-eu-year", routeAccessContext, <RefuelEuWorkspace />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu-year-calculation")}
                    element={guardRoute("fuel-refuel-eu-year-calculation", routeAccessContext, <LegacyRefuelEuRedirect />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu-year-airports")}
                    element={guardRoute("fuel-refuel-eu-year-airports", routeAccessContext, <RefuelEuWorkspace forcedSection="airports" />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu-year-data-quality")}
                    element={guardRoute("fuel-refuel-eu-year-data-quality", routeAccessContext, <RefuelEuWorkspace forcedSection="data-quality" />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu-year-delta-reasons")}
                    element={guardRoute("fuel-refuel-eu-year-delta-reasons", routeAccessContext, <RefuelEuWorkspace forcedSection="delta-reasons" />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu-year-scope")}
                    element={guardRoute("fuel-refuel-eu-year-scope", routeAccessContext, <RefuelEuWorkspace forcedSection="scope" />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu-year-api-management")}
                    element={guardRoute("fuel-refuel-eu-year-api-management", routeAccessContext, <RefuelEuWorkspace forcedSection="api-management" />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-refuel-eu-year-reports")}
                    element={guardRoute("fuel-refuel-eu-year-reports", routeAccessContext, <RefuelEuWorkspace forcedSection="reports" />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-assurance")}
                    element={guardRoute("fuel-assurance", routeAccessContext, <FuelAssuranceOverview />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-assurance-flights")}
                    element={guardRoute("fuel-assurance-flights", routeAccessContext, <FuelAssuranceFlights />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-assurance-flight")}
                    element={guardRoute("fuel-assurance-flight", routeAccessContext, <FuelAssuranceFlightExplorer />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-assurance-data-quality")}
                    element={guardRoute(
                      "fuel-assurance-data-quality",
                      routeAccessContext,
                      <FuelAssuranceDataQuality />,
                    )}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-assurance-entities")}
                    element={guardRoute("fuel-assurance-entities", routeAccessContext, <FuelAssuranceEntities />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-assurance-trends")}
                    element={guardRoute("fuel-assurance-trends", routeAccessContext, <FuelAssuranceTrends />)}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-assurance-opportunities")}
                    element={guardRoute(
                      "fuel-assurance-opportunities",
                      routeAccessContext,
                      <FuelAssuranceOpportunities />,
                    )}
                  />
                  <Route
                    path={getRelativeRoutePath("fuel-assurance-scf")}
                    element={guardRoute("fuel-assurance-scf", routeAccessContext, <FuelAssuranceScfReadiness />)}
                  />
                </Route>
                <Route
                  path={getRoutePath("refuel-eu")}
                  element={guardRoute(
                    "refuel-eu",
                    routeAccessContext,
                    <PreserveQueryRedirect to={getRouteRedirect("refuel-eu")} />,
                  )}
                />
                <Route
                  path={getRoutePath("refuel-eu-unjustified")}
                  element={guardRoute(
                    "refuel-eu-unjustified",
                    routeAccessContext,
                    <PreserveQueryRedirect to={getRouteRedirect("refuel-eu-unjustified")} />,
                  )}
                />
                <Route path={getRoutePath("fleets")} element={guardRoute("fleets", routeAccessContext, <Fleets />)} />
                <Route path={getRoutePath("ops")} element={guardRoute("ops", routeAccessContext, <Ops />)} />
                <Route path={getRoutePath("journey-logs")} element={guardRoute("journey-logs", routeAccessContext, <JourneyLogs />)} />
                <Route path={getRoutePath("journey-log")} element={guardRoute("journey-log", routeAccessContext, <JourneyLogs />)} />
                <Route path={getRoutePath("ops-transfer-delay")} element={guardRoute("ops-transfer-delay", routeAccessContext, <OpsTransferDelay />)} />
                <Route path={getRoutePath("ops-distribution")} element={guardRoute("ops-distribution", routeAccessContext, <OpsEmailDistribution />)} />
                <Route path={getRoutePath("profile")} element={guardRoute("profile", routeAccessContext, <UserProfile />)} />
                <Route
                  path={getRoutePath("operators-legacy")}
                  element={guardRoute("operators-legacy", routeAccessContext, <Navigate to={getRouteRedirect("operators-legacy")} replace />)}
                />
                <Route
                  path={getRoutePath("admin")}
                  element={guardRoute("admin", routeAccessContext, <AdminLayout isSuperAdmin={isSuperAdmin} canManageUsers={canManageUsers} />)}
                >
                  <Route index element={<AdminOverview />} />
                  <Route
                    path={getRelativeRoutePath("admin-users")}
                    element={guardRoute("admin-users", routeAccessContext, isSuperAdmin ? <AdminUsers withinAdminLayout /> : <Users withinAdminLayout />)}
                  />
                  <Route
                    path={getRelativeRoutePath("admin-customer-data-share")}
                    element={guardRoute("admin-customer-data-share", routeAccessContext, <AdminCustomerDataShare withinAdminLayout />)}
                  />
                  <Route
                    path={getRelativeRoutePath("admin-operators")}
                    element={guardRoute("admin-operators", routeAccessContext, <AdminOperatorSettings withinAdminLayout />)}
                  />
                  <Route
                    path={getRelativeRoutePath("admin-operator-settings")}
                    element={guardRoute("admin-operator-settings", routeAccessContext, <AdminOperatorSettings withinAdminLayout />)}
                  />
                  <Route
                    path={getRelativeRoutePath("admin-operator-services")}
                    element={guardRoute("admin-operator-services", routeAccessContext, <AdminOperatorServices withinAdminLayout />)}
                  />
                  <Route
                    path={getRelativeRoutePath("admin-accounting")}
                    element={guardRoute("admin-accounting", routeAccessContext, <AdminAccounting />)}
                  />
                  <Route
                    path={getRelativeRoutePath("admin-leon-transfer")}
                    element={guardRoute("admin-leon-transfer", routeAccessContext, <AdminLeonTransfer />)}
                  />
                  <Route path={getRelativeRoutePath("admin-leon-id")} element={guardRoute("admin-leon-id", routeAccessContext, <AdminLeonId />)} />
                  <Route
                    path={getRelativeRoutePath("admin-email-distribution")}
                    element={guardRoute("admin-email-distribution", routeAccessContext, <AdminEmailDistribution />)}
                  />
                  <Route
                    path={getRelativeRoutePath("admin-email-footer")}
                    element={guardRoute("admin-email-footer", routeAccessContext, <AdminEmailFooter />)}
                  />
                  <Route
                    path={getRelativeRoutePath("admin-smart-ofp-legacy")}
                    element={guardRoute("admin-smart-ofp-legacy", routeAccessContext, <Navigate to={getRouteRedirect("admin-smart-ofp-legacy")} replace />)}
                  />
                  <Route path={getRelativeRoutePath("admin-pdf")} element={guardRoute("admin-pdf", routeAccessContext, <AdminPdf withinAdminLayout />)} />
                  <Route path={getRelativeRoutePath("admin-status")} element={guardRoute("admin-status", routeAccessContext, <AdminStatus />)} />
                  <Route path={getRelativeRoutePath("admin-settings")} element={guardRoute("admin-settings", routeAccessContext, <AdminSettings />)} />
                  <Route path={getRelativeRoutePath("admin-logs")} element={guardRoute("admin-logs", routeAccessContext, <AdminLogs withinAdminLayout />)} />
                  <Route
                    path={getRelativeRoutePath("admin-mail-settings")}
                    element={guardRoute("admin-mail-settings", routeAccessContext, <AdminMailSettings />)}
                  />
                  <Route path={getRelativeRoutePath("admin-maintenance")} element={guardRoute("admin-maintenance", routeAccessContext, <AdminMaintenance />)} />
                  <Route path={getRelativeRoutePath("admin-nextclouds")} element={guardRoute("admin-nextclouds", routeAccessContext, <AdminNextclouds />)} />
                  <Route
                    path={getRelativeRoutePath("admin-fallback")}
                    element={guardRoute("admin-fallback", routeAccessContext, <Navigate to={getRouteRedirect("admin-fallback")} replace />)}
                  />
                </Route>
                <Route path={getRoutePath("library-user-report")} element={guardRoute("library-user-report", routeAccessContext, <LibraryReportRedirect />)} />
                <Route path={getRoutePath("not-found")} element={guardRoute("not-found", routeAccessContext, <NotFound />)} />
                </Routes>
              </Suspense>
            </AppRouteFrame>
          </LocalizationProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </AppThemeProvider>
  );
};

export default App;
